import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import { deleteOnboardingDocument as deleteOnboardingDocumentRequest } from 'src/services/client';
import { fetchSuiteCounts } from 'src/store/actions/suites';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';

const SUITE_ITEM_DATA = 'SuiteAssets';
const ONBOARDING_DOCUMENT_TYPE = 'Onboarding';

interface UseSuiteDocumentsAPIOptions {
  suiteId: string;
  userId: string;
  roleName: string;
  customUploadErrorMessage?: string;
}

export interface UseSuiteDocumentsAPIReturn
  extends Omit<UseFaqsNotesDocumentsAPIReturn, 'deleteDocument'> {
  deleteDocument: (document: any) => Promise<boolean>;
}

export const useSuiteDocumentsAPI = ({
  suiteId,
  userId,
  roleName,
  customUploadErrorMessage,
}: UseSuiteDocumentsAPIOptions): UseSuiteDocumentsAPIReturn => {
  const dispatch = useDispatch();

  const base: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({
    itemData: SUITE_ITEM_DATA,
    customUploadErrorMessage,
  });

  const refreshSuiteCounts = (): void => {
    dispatch(fetchSuiteCounts(suiteId, roleName));
  };

  const deleteOnboardingDoc = async (documentId: string): Promise<boolean> => {
    try {
      const response: any = await deleteOnboardingDocumentRequest(
        documentId,
        userId
      );

      if (!(response.ok || response.status === 200)) {
        toast.error('ⓘ   Documents deleting failed.', {
          autoClose: 2000,
          position: 'top-center',
        });

        return false;
      }

      toast.success('ⓘ   Documents has been deleted.', {
        autoClose: 10000,
        position: 'top-center',
      });

      base.removeDocumentFromState(documentId);
      return true;
    } catch (error) {
      toast.error('ⓘ   Documents deleting failed.', {
        autoClose: 2000,
        position: 'top-center',
      });
      console.error('Onboarding document delete error:', error);
      return false;
    }
  };

  const deleteDocument = async (document: any): Promise<boolean> => {
    const isOnboardingDoc: boolean =
      document.documentType === ONBOARDING_DOCUMENT_TYPE;

    const didDelete: boolean = isOnboardingDoc
      ? await deleteOnboardingDoc(document.id)
      : await base.deleteDocument(document.id);

    if (didDelete) {
      refreshSuiteCounts();
    }

    return didDelete;
  };

  const saveDocument = async (
    document: DocumentFormData,
    entityId: string,
    savedByUserId: string
  ): Promise<void> => {
    await base.saveDocument(document, entityId, savedByUserId);
    refreshSuiteCounts();
  };

  return {
    ...base,
    saveDocument,
    deleteDocument,
  };
};
